// src/components/games/crossroads/types.ts
// Type definitions for Crossroads: The Divergent Realms TCG

export enum ElementType {
  FIRE = 'fire',
  FAE = 'fae',
  EARTH = 'earth',
  DARK = 'dark',
  WATER = 'water',
  LIGHT = 'light',
  WIND = 'wind',
  NEUTRAL = 'neutral',
}

export enum CardType {
  COMMANDER = 'commander',
  CREATURE = 'creature',
  ART = 'art',
  MANA_SOURCE = 'mana_source',
  ENVIRONMENT = 'environment',
}

export enum Rarity {
  COMMON = 'common',
  UNCOMMON = 'uncommon',
  RARE = 'rare',
}

export enum Phase {
  ENVIRONMENT = 'environment',
  OPENING = 'opening',
  RISING_ACTION = 'rising_action',
  CONFLICT = 'conflict',
  RESOLUTION = 'resolution',
  END_OF_ROUND = 'end_of_round',
}

export enum AbilityKeyword {
  FLYING = 'flying',
  BLOCK = 'block',
  FIRST_STRIKE = 'first_strike',
  DOUBLE_STRIKE = 'double_strike',
  DEATHTOUCH = 'deathtouch',
  LIFELINK = 'lifelink',
  VIGILANCE = 'vigilance',
  TRAMPLE = 'trample',
  HASTE = 'haste',
  DEFENDER = 'defender',
  REACH = 'reach',
}

export enum EnvironmentSeverity {
  MILD = 'mild',
  MODERATE = 'moderate',
  CHAOS = 'chaos',
  GAME_CHANGING = 'game_changing',
}

// Mana cost represented as a map of element types to amounts
export type ManaCost = Partial<Record<ElementType, number>>;

// Mana pool tracks available mana for a player
export type ManaPool = Partial<Record<ElementType, number>>;

// Base card interface
export interface BaseCard {
  id: string;
  name: string;
  type: CardType;
  flavorText?: string;
  imageUrl?: string;
}

// Commander card
export interface CommanderCard extends BaseCard {
  type: CardType.COMMANDER;
  health: number;
  elementType: ElementType;
  abilities: CommanderAbility[];
  pronouns: string;
  subtitle: string;
}

// Commander ability
export interface CommanderAbility {
  id: string;
  cost: ManaCost;
  tapCost?: boolean; // True if ability requires tapping the commander
  description: string;
  effect: (game: GameState, playerId: number) => void;
  usesPerTurn?: number;
}

// Creature card
export interface CreatureCard extends BaseCard {
  type: CardType.CREATURE;
  cost: ManaCost;
  power: number;
  health: number;
  rarity: Rarity;
  elementType?: ElementType;
  keywords: AbilityKeyword[];
  abilities?: CreatureAbility[];
  subtype?: string; // e.g., "Imp", "Elemental", "Warrior"
}

// Creature ability
export interface CreatureAbility {
  id: string;
  cost?: ManaCost;
  tapCost?: boolean; // True if ability requires tapping the creature
  description: string;
  trigger?: 'enter' | 'defeat' | 'damage' | 'attack' | 'block' | 'turn_start' | 'turn_end' | 'activated';
  effect: (game: GameState, creatureId: string, targets?: TargetSelection) => void;
}

// Art card (spell)
export interface ArtCard extends BaseCard {
  type: CardType.ART;
  cost: ManaCost;
  rarity: Rarity;
  effect: (game: GameState, playerId: number, targets?: TargetSelection) => void;
  targetType?: 'creature' | 'player' | 'any' | 'none';
  targetCount?: number;
}

// Mana Source card
export interface ManaSourceCard extends BaseCard {
  type: CardType.MANA_SOURCE;
  cost?: ManaCost;
  rarity: Rarity;
  elementType: ElementType;
  manaGenerated: ManaPool;
  abilities?: ManaSourceAbility[];
}

// Mana source ability
export interface ManaSourceAbility {
  id: string;
  description: string;
  effect: (game: GameState, playerId: number) => void;
}

// Environment card
export interface EnvironmentCard extends BaseCard {
  type: CardType.ENVIRONMENT;
  severity: EnvironmentSeverity;
  deckTheme: string; // e.g., "Enchanted Forest", "Scorching Desert"
  description?: string; // What the effect does (shown in event log)
  effect: (game: GameState) => void;
  targetType?: 'universal' | 'first_player' | 'random';
}

// Card union type
export type Card = CommanderCard | CreatureCard | ArtCard | ManaSourceCard | EnvironmentCard;

// Creature on battlefield
export interface BattlefieldCreature {
  instanceId: string; // Unique instance ID
  cardId: string; // Reference to card definition
  ownerId: number; // Player ID who owns this
  power: number; // Current power (can be modified)
  health: number; // Current health (can be damaged)
  maxHealth: number; // Maximum health
  isTapped: boolean;
  summoningSick: boolean; // Can't attack first turn
  keywords: AbilityKeyword[];
  counters: CreatureCounters;
  activeAbilities: string[]; // IDs of triggered abilities
  copiedAbilities?: CreatureAbility[]; // Abilities copied from other creatures
  currentHealth: number; // Alias for health (for backwards compatibility)
}

// Counters on creatures
export interface CreatureCounters {
  plusOnePlusOne: number; // +1/+1 counters
  minusOneMinusOne: number; // -1/-1 counters
}

// Token creatures
export interface TokenCreature extends BattlefieldCreature {
  isToken: true;
  tokenType: string; // e.g., "Sapling", "Skeleton", "Demon Cat"
}

// Mana source on battlefield
export interface BattlefieldManaSource {
  instanceId: string;
  cardId: string;
  ownerId: number;
  isTapped: boolean;
}

// Target selection for spells/abilities
export interface TargetSelection {
  creatures?: string[]; // Instance IDs
  players?: number[]; // Player IDs
}

// Player state
export interface PlayerState {
  id: number;
  name: string;
  deck: string[]; // Card IDs
  hand: string[]; // Card IDs
  discard: string[]; // Card IDs
  exiled: string[]; // Card IDs
  commander: CommanderState;
  commanderHealth?: number; // Alias for commander.currentHealth (for convenience)
  battlefield: {
    creatures: BattlefieldCreature[];
    manaSources: BattlefieldManaSource[];
  };
  manaPool: ManaPool;
  hasPriority: boolean;
  hasPlayedManaThisTurn: boolean;
  cardCostReductions?: Record<string, number>; // Card ID -> cost reduction amount (for bounced creatures, etc.)
  sideboard?: string[]; // Card IDs in sideboard (for best-of-3 matches)
}

// Commander state
export interface CommanderState {
  cardId: string;
  currentHealth: number;
  maxHealth: number;
  tapped: boolean;
  abilityUsesThisTurn: Record<string, number>; // Ability ID -> uses
}

// Game event log entry
export interface GameEvent {
  type: 'dice_roll' | 'shield_added' | 'block_trigger' | 'creature_defeated' | 'ability_activated';
  message: string;
  timestamp: number;
  playerId?: number;
}

// Chat message
export interface ChatMessage {
  playerId: number;
  playerName: string;
  message: string;
  timestamp: number;
}

// Ongoing effect types
export enum OngoingEffectType {
  POWER_HEALTH_BUFF = 'power_health_buff',
  KEYWORD_GRANT = 'keyword_grant',
  CANT_ATTACK = 'cant_attack',
  CANT_BLOCK = 'cant_block',
  CANT_TAP = 'cant_tap',
  CANT_UNTAP = 'cant_untap',
  COST_MODIFIER = 'cost_modifier',
  MANA_MODIFIER = 'mana_modifier',
  COMBAT_DAMAGE_MODIFIER = 'combat_damage_modifier',
  ENTERS_TAPPED = 'enters_tapped',
  MUST_ATTACK = 'must_attack', // Creature must attack if able
  CANT_USE_ABILITIES = 'cant_use_abilities', // Can't activate abilities (commander/creature)
  CONTROL_SWAP = 'control_swap', // Creature changes controller temporarily
  PLAY_FROM_GRAVEYARD = 'play_from_graveyard', // Can play specific cards from graveyard
  STAT_TRANSFORMATION = 'stat_transformation', // Creature transformed into copy of another (stores original stats)
}

export enum OngoingEffectDuration {
  END_OF_TURN = 'end_of_turn',
  END_OF_ROUND = 'end_of_round',
  PERMANENT = 'permanent',
  UNTIL_REMOVED = 'until_removed',
}

// Ongoing effect instance
export interface OngoingEffect {
  id: string; // Unique effect ID
  type: OngoingEffectType;
  duration: OngoingEffectDuration;
  sourceCardId?: string; // Card that created this effect
  targetId: string; // Creature instance ID, player ID, or 'all'
  targetType: 'creature' | 'player' | 'all_creatures' | 'all_players' | 'global';
  createdByPlayerId: number; // Player who created the effect
  createdAtTurn: number; // Turn number when created
  createdAtPhase: Phase; // Phase when created
  // Effect-specific data
  powerModifier?: number;
  healthModifier?: number;
  keyword?: AbilityKeyword;
  costModifier?: number;
  manaModifier?: number;
  damageModifier?: number;
  filterElementType?: ElementType; // For cost/mana modifiers that only affect certain types
  invertElementFilter?: boolean; // If true, applies to all EXCEPT filterElementType
  filterCardType?: CardType; // For cost modifiers that only affect certain card types
  // Advanced effect data
  originalOwnerId?: number; // For CONTROL_SWAP: original owner of the creature
  newControllerId?: number; // For CONTROL_SWAP: new controller of the creature
  allowedCardId?: string; // For PLAY_FROM_GRAVEYARD: specific card that can be played
  // For STAT_TRANSFORMATION: original stats to restore
  originalPower?: number;
  originalHealth?: number;
  originalMaxHealth?: number;
  originalKeywords?: AbilityKeyword[];
  originalCardId?: string; // Original card ID before transformation
}

// Modal Choice System
export enum ModalChoiceType {
  BINARY = 'binary', // Yes/No, Pay/Don't Pay
  CHOOSE_ONE = 'choose_one', // Choose one option from a list
  CHOOSE_MULTIPLE = 'choose_multiple', // Choose N options from a list
  CHOOSE_CARDS = 'choose_cards', // Choose cards from a zone (hand, graveyard, etc.)
  ORDER_ITEMS = 'order_items', // Order/prioritize a list of items
  REVEAL_INFO = 'reveal_info', // Display information (e.g., revealed cards) with OK button
  CONFIRM = 'confirm', // Confirmation dialog for important actions (Confirm/Cancel)
}

export interface ModalChoiceOption {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
  cost?: ManaCost; // For options that require payment
}

export interface ModalChoice {
  id: string; // Unique choice ID
  type: ModalChoiceType;
  title: string;
  description?: string;
  options?: ModalChoiceOption[]; // For CHOOSE_ONE, CHOOSE_MULTIPLE
  minChoices?: number; // For CHOOSE_MULTIPLE
  maxChoices?: number; // For CHOOSE_MULTIPLE
  cardZone?: 'hand' | 'graveyard' | 'battlefield' | 'deck'; // For CHOOSE_CARDS
  cardFilter?: (card: Card) => boolean; // Filter which cards can be chosen
  revealedCards?: { id: string; name: string; imageUrl?: string }[]; // For REVEAL_INFO: cards to display
  playerId: number; // Player who needs to make the choice
  sourceCardId?: string; // Card that triggered this choice
  sourceAbilityId?: string; // Ability that triggered this choice
  onComplete: (selectedIds: string[]) => void; // Callback with selected option IDs
  onCancel?: () => void; // Optional cancel callback
  timeoutSeconds?: number; // Auto-select default after timeout
  defaultChoice?: string[]; // Default selection if timeout occurs
}

// Trigger System
export enum TriggerType {
  ON_CREATURE_ENTER = 'on_creature_enter', // When a creature enters the battlefield
  ON_CREATURE_DEFEAT = 'on_creature_defeat', // When a creature is defeated
  ON_CREATURE_ATTACK = 'on_creature_attack', // When a creature attacks
  ON_CREATURE_BLOCK = 'on_creature_block', // When a creature blocks
  ON_CREATURE_DAMAGE = 'on_creature_damage', // When a creature deals damage
  ON_ART_CAST = 'on_art_cast', // When an Art spell is cast
  ON_TURN_START = 'on_turn_start', // At the start of a turn
  ON_TURN_END = 'on_turn_end', // At the end of a turn
  ON_PHASE_CHANGE = 'on_phase_change', // When the phase changes
  ON_MANA_SOURCE_PLAYED = 'on_mana_source_played', // When a mana source is played
}

export interface TriggerContext {
  creatureId?: string; // For creature-related triggers
  playerId?: number; // Player who triggered this
  targetId?: string; // Target of the trigger (e.g., damaged creature)
  damage?: number; // For damage triggers
  cardId?: string; // For card-related triggers
  phase?: Phase; // For phase change triggers
}

export interface Trigger {
  id: string; // Unique trigger ID
  type: TriggerType;
  sourceCardId?: string; // Card that created this trigger
  sourceAbilityId?: string; // Ability that created this trigger
  createdByPlayerId: number; // Player who created the trigger
  duration: OngoingEffectDuration; // How long this trigger lasts
  createdAtTurn: number; // Turn when created
  createdAtPhase: Phase; // Phase when created
  effect: (game: GameState, context: TriggerContext) => void; // Callback when triggered
  condition?: (game: GameState, context: TriggerContext) => boolean; // Optional condition check
}

// Pending target selection request
export interface PendingTargetSelection {
  id: string; // Unique request ID
  playerId: number; // Player who needs to select targets
  title: string; // Title for the selection UI
  description: string; // Description of what's being targeted
  targetType: 'creature' | 'player' | 'any'; // What can be targeted
  minTargets: number; // Minimum number of targets
  maxTargets: number; // Maximum number of targets
  filter?: (targetId: string, game: GameState) => boolean; // Optional filter for valid targets
  sourceCardId?: string; // Card requesting the targets
  sourceAbilityId?: string; // Ability requesting the targets
  onComplete: (selection: TargetSelection) => void; // Callback with selected targets
  onCancel?: () => void; // Optional cancel callback
}

// Damage event tracking
export interface DamageEvent {
  sourceId: string; // Creature instance ID that dealt damage
  targetId: string; // Target that took damage (creature instance ID or player ID)
  targetType: 'creature' | 'player';
  amount: number;
  timestamp: number;
}

// Game state
export interface GameState {
  players: PlayerState[];
  currentPlayerIndex: number;
  firstPlayerIndex: number; // Rotates each round
  currentRound: number;
  currentPhase: Phase;
  environmentDeck: string[]; // Card IDs
  activeEnvironmentCard: string | null; // Card ID
  cardInstances: Record<string, Card>; // instanceId -> Card
  turnOrder: number[]; // Player IDs in turn order
  gameOver: boolean;
  winner: number | null; // Player ID
  lastDiceRoll?: {
    value: number;
    playerId: number;
    rerollable: boolean;
    // For undo/redo support
    effectType?: 'damage' | 'heal' | 'none';
    targetId?: string; // Target that was affected (player_X or creature instanceId)
    targetType?: 'player' | 'creature';
    amount?: number; // Original amount applied
  }; // Track last dice roll for reroll ability
  preventionShields?: { targetId: string; targetType: 'player' | 'creature'; amount: number }[]; // Damage prevention shields
  blockAssignments?: Record<string, string>; // Temporary: blockerId -> attackerId (for block triggers)
  creaturesDefeatedThisTurn?: { instanceId: string; defeatedBy?: string }[]; // Track creature defeats for card abilities
  eventLog?: GameEvent[]; // Game event log for UX feedback (last 10 events)
  chatMessages?: ChatMessage[]; // Player chat messages
  ongoingEffects?: OngoingEffect[]; // Active ongoing effects
  pendingModalChoice?: ModalChoice; // Active modal choice awaiting player decision
  pendingTargetSelection?: PendingTargetSelection; // Active target selection awaiting player decision
  triggers?: Trigger[]; // Active triggers waiting to fire on specific events
  attackTargets?: Record<string, { targetId: string; targetType: 'creature' | 'player' }>; // Track what each attacker is targeting (attackerId -> target)
  damageEventsThisTurn?: DamageEvent[]; // Track all damage dealt this turn for trigger purposes
}

// Type advantage system
export const TYPE_ADVANTAGES: Record<ElementType, ElementType[]> = {
  [ElementType.FIRE]: [ElementType.WIND, ElementType.EARTH],
  [ElementType.WIND]: [ElementType.WATER, ElementType.LIGHT],
  [ElementType.WATER]: [ElementType.FIRE, ElementType.DARK],
  [ElementType.EARTH]: [ElementType.WIND, ElementType.LIGHT],
  [ElementType.LIGHT]: [ElementType.DARK, ElementType.FAE],
  [ElementType.DARK]: [ElementType.EARTH, ElementType.FAE],
  [ElementType.FAE]: [ElementType.FIRE, ElementType.WATER],
  [ElementType.NEUTRAL]: [], // Neutral has no type advantages
};

// Helper function to check type advantage
export function hasTypeAdvantage(
  attackerType: ElementType | undefined,
  defenderType: ElementType | undefined
): boolean {
  if (!attackerType || !defenderType) return false;
  return TYPE_ADVANTAGES[attackerType]?.includes(defenderType) || false;
}

// Combat declaration
export interface CombatDeclaration {
  attackerId: string; // Creature instance ID
  targetId: string; // Creature instance ID or player ID
  targetType: 'creature' | 'player';
}

// Block declaration
export interface BlockDeclaration {
  blockerId: string; // Creature instance ID
  attackerId: string; // Creature instance ID
}

// Game action types
export enum GameAction {
  DRAW_CARD = 'draw_card',
  PLAY_CARD = 'play_card',
  ACTIVATE_ABILITY = 'activate_ability',
  DECLARE_ATTACKERS = 'declare_attackers',
  DECLARE_BLOCKERS = 'declare_blockers',
  PASS_PRIORITY = 'pass_priority',
  END_PHASE = 'end_phase',
  MULLIGAN = 'mulligan',
}

// Deck definition
export interface DeckDefinition {
  name: string;
  elementType: ElementType;
  commanderId: string;
  cards: { cardId: string; count: number }[];
  sideboard?: { cardId: string; count: number }[]; // Optional sideboard for best-of-3 matches
}
